/* ============================================================
   NOVA — AHSAN TARIQ COUTURE
   Private couture intelligence.

   ------------------------------------------------------------
   LINEAGE
   ------------------------------------------------------------
   This file is the V12 NOVA brain, preserved and extended — not
   a replacement for it.

   What was kept from V12, intact:
     the full client profile (occasion, role, event, timeline,
     location, outfit, style, work level, colour, height, weight,
     complexion, size, fabric, budget range), the guided discovery
     flow that asks only for what is still missing, the delivery
     feasibility assessment, the outfit-direction and colour
     reasoning, the conversation stage machine, persistent history,
     bilingual English / Roman Urdu output, image and camera input,
     voice input, and the WhatsApp handoff that carries the whole
     brief to the atelier.

   What was replaced:
     only the language layer. V12 normalised input by looking the
     whole sentence up in a frozen table of ~16,000 literal phrases
     (950 KB). That table shipped on every page and only fired on
     strings someone had enumerated. It is now a fold → alias →
     bounded-fuzzy → scored-concept pipeline that recognises the
     same concepts from arbitrary word order and misspellings, in a
     fraction of the bytes, and generalises to phrasings nobody
     listed.

   What was added:
     a contextual notification invitation with real Allow / Not now
     controls, a short cinematic wake transition, focus management,
     and integration with the AURUM bag.

   ------------------------------------------------------------
   CONTENTS
   ------------------------------------------------------------
      1  Config
      2  Normalisation
      3  Lexicon and concept scoring
      4  Language detection and bilingual output
      5  Profile and persistent history
      6  Fact extraction
      7  Timeline
      8  Budget
      9  Delivery assessment
     10  Catalogue
     11  Recommendation reasoning
     12  View
     13  Discovery flow
     14  Answer handling
     15  Service and experience intents
     16  Notifications
     17  Atelier handoff
     18  Image, camera and voice
     19  Panel, wake transition, boot

   No product, price, collection, review, social destination or
   delivery promise is ever invented. Catalogue data comes from
   Shopify; destinations come from theme settings; timelines are
   described as windows requiring Design Team confirmation, which
   is how V12 phrased them.
   ============================================================ */

(function () {
  'use strict';

  var root = document.querySelector('[data-at-nova]');
  if (!root) return;

  /* ==========================================================
     1. CONFIG
     Read from data attributes. Nothing is hardcoded, so a
     destination that is not configured simply does not exist.
     ========================================================== */

  var CONFIG = {
    whatsapp: (root.dataset.novaWhatsapp || '').replace(/\D/g, ''),
    instagram: root.dataset.novaInstagram || '',
    map: root.dataset.novaMap || '',
    worldUrl: root.dataset.novaWorld || '/pages/world-of-ahsan-tariq',
    searchUrl: root.dataset.novaSearch || '/search',
    accountUrl: root.dataset.novaAccount || '/account',
    apiUrl: root.dataset.novaApi || '',
    apiEnabled: root.dataset.novaApiEnabled === 'true',
    moneyFormat: root.dataset.novaMoneyFormat || '{{amount}}'
  };

  var STORAGE_KEY = 'at_nova_client_v14';
  var HISTORY_KEY = 'at_nova_history_v14';
  var NOTIFY_KEY = 'at_nova_notify_v14';

  var HISTORY_LIMIT = 80;
  var HISTORY_REPLAY = 30;
  var MAX_PRODUCTS = 4;

  /* ==========================================================
     2. NORMALISATION
     ========================================================== */

  /**
   * Compact alias map.
   *
   * Only entries fuzzy matching cannot reach belong here: true
   * synonyms, Roman Urdu equivalents, and transliterations more
   * than two edits from the canonical form. Ordinary misspellings
   * are handled by correct() and must not be listed.
   */
  var ALIASES = {
    // greetings / courtesies
    salam: 'hello', assalam: 'hello', assalamualaikum: 'hello', aoa: 'hello',
    slam: 'hello', hey: 'hello', hi: 'hello', yo: 'hello', sup: 'hello',
    hlo: 'hello', helo: 'hello', shukriya: 'thanks', shukria: 'thanks',
    khuda: 'bye', hafiz: 'bye', allahhafiz: 'bye',

    // affirm / deny
    haan: 'yes', han: 'yes', ji: 'yes', jee: 'yes', bilkul: 'yes',
    theek: 'yes', thik: 'yes', ok: 'yes', okay: 'yes', sure: 'yes',
    yeah: 'yes', yep: 'yes', acha: 'yes', achha: 'yes',
    nahi: 'no', nai: 'no', nahin: 'no', nope: 'no', nah: 'no',

    // garments
    shirwani: 'sherwani', shervani: 'sherwani', sherwany: 'sherwani',
    kot: 'coat', kurte: 'kurta', kurtay: 'kurta',
    pajama: 'pyjama', pyjamas: 'pyjama', pajamas: 'pyjama',
    salwar: 'shalwar', qameez: 'kameez',
    vest: 'waistcoat', tux: 'tuxedo', blazer: 'jacket',

    // occasions
    shadi: 'wedding', shaadi: 'wedding', shadhi: 'wedding', biyah: 'wedding',
    barat: 'baraat', braat: 'baraat', valima: 'walima', reception: 'walima',
    nikah: 'nikkah', nikaah: 'nikkah', nikka: 'nikkah',
    mehendi: 'mehndi', mayun: 'mehndi', mayoun: 'mehndi',
    dholki: 'mehndi', sangeet: 'mehndi',
    mangni: 'engagement', mangani: 'engagement',

    // roles / relations
    dulha: 'groom', dulhay: 'groom', dulhan: 'bride',
    bhai: 'brother', behn: 'sister', behen: 'sister',
    dost: 'friend', larka: 'groom',

    // qualities
    bharwa: 'heavy', zyada: 'heavy', bohat: 'very', bahut: 'very',
    halka: 'light', thora: 'medium', thoda: 'medium',
    shahi: 'royal', maharaja: 'royal', badshah: 'royal',
    saada: 'simple', sada: 'simple', decent: 'elegant',

    // actions
    dikhao: 'show', dikhaye: 'show', dikhayen: 'show', dekhna: 'show',
    chahiye: 'want', chahye: 'want', chaiye: 'want', chahta: 'want',
    karo: 'do', kro: 'do', batao: 'tell', btao: 'tell',
    pehnu: 'wear', pehnun: 'wear', pahnu: 'wear',
    lena: 'buy', kharidna: 'buy', khareedna: 'buy',

    // questions
    kitna: 'how much', kitne: 'how much', kitni: 'how much',
    lagega: 'time take', lagegi: 'time take', lagay: 'time take',
    kya: 'what', kia: 'what', kaha: 'where', kahan: 'where',
    kab: 'when', kaise: 'how', kesay: 'how',

    // service
    qeemat: 'price', keemat: 'price', daam: 'price', rate: 'price',
    naap: 'measurement', maap: 'measurement',
    pohnchega: 'delivery', milega: 'delivery',
    banwana: 'custom', silwana: 'custom', tailor: 'custom',
    dukan: 'store', shop: 'store', studio: 'store',
    banda: 'human', insan: 'human', bandey: 'human',

    // time
    din: 'days', hafta: 'week', hafte: 'week', haftay: 'week',
    mahina: 'month', mahine: 'month', mahinay: 'month',
    kal: 'tomorrow', agle: 'next', aglay: 'next',

    // money units
    lakh: 'lac', lakhs: 'lac', hazar: 'thousand', hazaar: 'thousand'
  };

  function fold(input) {
    return String(input || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/[’'`]/g, '')
      .replace(/[^a-z0-9\s]/g, ' ')
      .replace(/(.)\1{2,}/g, '$1$1')
      .replace(/\s+/g, ' ')
      .trim();
  }

  /** Bounded Levenshtein; returns Infinity once it exceeds max. */
  function editDistance(a, b, max) {
    if (a === b) return 0;
    if (Math.abs(a.length - b.length) > max) return Infinity;

    var prev = [];
    var i;
    for (i = 0; i <= b.length; i++) prev[i] = i;

    for (i = 1; i <= a.length; i++) {
      var current = [i];
      var rowMin = i;

      for (var j = 1; j <= b.length; j++) {
        var cost = a.charCodeAt(i - 1) === b.charCodeAt(j - 1) ? 0 : 1;
        current[j] = Math.min(current[j - 1] + 1, prev[j] + 1, prev[j - 1] + cost);
        if (current[j] < rowMin) rowMin = current[j];
      }

      if (rowMin > max) return Infinity;
      prev = current;
    }

    return prev[b.length];
  }

  /**
   * Tolerance scales with length. Short words must match exactly,
   * otherwise "kam" and "kaam" collapse and the light/heavy
   * distinction is lost.
   */
  function tolerance(word) {
    if (word.length <= 4) return 0;
    if (word.length <= 7) return 1;
    return 2;
  }

  function tokenise(text) {
    var out = [];

    fold(text)
      .split(' ')
      .filter(Boolean)
      .forEach(function (word) {
        var singular = word.replace(/(.)\1/g, '$1');
        var mapped = ALIASES[word] || ALIASES[singular] || word;

        // An alias may expand to several words ("kitna" -> "how much").
        if (mapped.indexOf(' ') === -1) out.push(mapped);
        else Array.prototype.push.apply(out, mapped.split(' '));
      });

    return out;
  }

  /* ==========================================================
     3. LEXICON AND CONCEPT SCORING
     ========================================================== */

  var LEXICON = {
    // occasions
    wedding: ['wedding', 'marriage', 'function'],
    ownWedding: ['my wedding', 'meri wedding', 'my own wedding', 'i am the groom', 'im the groom', 'groom hoon', 'groom hun', 'apni wedding'],
    guestWedding: ['someone else wedding', 'friends wedding', 'friend wedding', 'cousin wedding', 'brother wedding', 'sister wedding', 'attending a wedding', 'guest'],
    baraat: ['baraat', 'main day', 'wedding day'],
    walima: ['walima'],
    nikkah: ['nikkah'],
    mehndi: ['mehndi'],
    engagement: ['engagement', 'ring ceremony'],
    party: ['party', 'dinner', 'gala', 'formal evening'],
    eid: ['eid'],

    // roles
    groom: ['groom'],
    bride: ['bride'],
    brother: ['brother of groom', 'brother of bride', 'best man'],
    guest: ['wedding guest', 'attending'],

    // garments
    sherwani: ['sherwani'],
    princeCoat: ['prince coat', 'princecoat', 'prince jacket'],
    suit: ['suit', 'tuxedo', 'two piece', 'three piece', 'jacket'],
    kurta: ['kurta', 'pyjama', 'shalwar', 'kameez'],
    waistcoat: ['waistcoat'],
    openJacket: ['open jacket', 'open coat', 'cape'],

    // embellishment
    heavy: ['heavy', 'loaded', 'full work', 'maximum work', 'statement', 'grand'],
    medium: ['medium', 'balanced', 'moderate'],
    light: ['light', 'minimal', 'simple', 'subtle', 'clean', 'less work', 'refined'],

    // style
    royal: ['royal', 'regal', 'king', 'majestic'],
    classy: ['classy', 'elegant', 'sophisticated', 'understated'],
    modern: ['modern', 'contemporary', 'trendy', 'sharp', 'new'],
    traditional: ['traditional', 'classic', 'heritage', 'desi', 'mughal'],

    // colour families
    ivory: ['ivory', 'cream', 'off white', 'champagne'],
    black: ['black', 'charcoal', 'deep'],
    gold: ['gold', 'golden', 'antique gold'],
    pastel: ['pastel', 'powder', 'blush', 'soft tones'],
    jewel: ['emerald', 'maroon', 'burgundy', 'navy', 'bottle green'],

    // complexion
    complexionLight: ['fair', 'light skin', 'complexion light'],
    complexionMedium: ['medium skin', 'complexion medium'],
    complexionWheatish: ['wheatish', 'complexion wheatish'],
    complexionDeep: ['dark skin', 'deep skin', 'complexion deep'],

    // fit
    slim: ['slim fit', 'slim', 'fitted', 'tapered'],
    regular: ['regular fit', 'comfortable fit', 'relaxed'],

    // service
    price: ['price', 'cost', 'how much', 'expensive', 'afford'],
    budget: ['budget'],
    delivery: ['delivery', 'shipping', 'how long', 'how much time', 'when ready', 'timeline', 'urgent', 'deadline', 'time take'],
    measurement: ['measurement', 'size', 'fitting', 'alteration', 'tailoring'],
    custom: ['custom', 'bespoke', 'made to measure', 'customise'],
    care: ['care', 'wash', 'clean', 'maintain', 'storage'],
    store: ['store', 'address', 'location', 'where are you', 'directions', 'visit', 'showroom'],
    human: ['human', 'person', 'design team', 'someone', 'call', 'whatsapp', 'talk to', 'sales'],
    appointment: ['appointment', 'consultation', 'booking', 'meet', 'schedule'],
    order: ['order status', 'my order', 'tracking', 'track'],
    account: ['my account', 'login', 'log in', 'sign in', 'customer account'],
    notifications: ['notifications', 'notification', 'notify me', 'alert me', 'updates'],
    international: ['international', 'overseas', 'abroad', 'uk', 'usa', 'canada', 'dubai', 'uae', 'australia', 'europe', 'saudi', 'qatar'],
    pakistan: ['pakistan', 'lahore', 'karachi', 'islamabad', 'gujranwala', 'rawalpindi', 'faisalabad'],
    world: ['world of ahsan tariq', 'behind the scenes', 'atelier stories', 'campaigns'],

    // conversation control
    recommend: ['recommend', 'suggest', 'help me choose', 'what should i wear', 'pick for me', 'choose for me', 'you decide', 'options'],
    show: ['show', 'see', 'browse', 'view', 'display', 'look'],
    more: ['more', 'other', 'another', 'else', 'next'],
    greeting: ['hello', 'good morning', 'good evening'],
    thanks: ['thanks', 'thank you', 'appreciated'],
    bye: ['bye', 'goodbye'],
    yes: ['yes'],
    no: ['no'],
    skip: ['skip', 'prefer not', 'rather not', 'pass'],
    flexible: ['flexible', 'no limit', 'open budget'],
    reset: ['start over', 'restart', 'reset', 'begin again', 'clear']
  };

  var TOKEN_INDEX = {};
  var PHRASE_INDEX = [];
  var VOCAB_SET = {};

  Object.keys(LEXICON).forEach(function (concept) {
    LEXICON[concept].forEach(function (surface) {
      var folded = fold(surface);

      folded.split(' ').forEach(function (word) {
        if (word) VOCAB_SET[word] = true;
      });

      if (folded.indexOf(' ') === -1) {
        (TOKEN_INDEX[folded] = TOKEN_INDEX[folded] || []).push(concept);
      } else {
        PHRASE_INDEX.push({ phrase: folded, concept: concept });
      }
    });
  });

  var VOCAB = Object.keys(VOCAB_SET);

  /**
   * Every vocabulary word within edit tolerance of a token.
   * Returning all near matches rather than one best match is
   * deliberate: "prnce" is one edit from both "price" and
   * "prince", and keeping both lets the phrase bonus decide, so
   * "prnce kot" resolves to prince coat rather than price.
   */
  function correct(token) {
    if (VOCAB_SET[token]) return [token];

    var limit = tolerance(token);
    if (!limit) return [token];

    var matches = [];
    var best = Infinity;

    for (var i = 0; i < VOCAB.length; i++) {
      var candidate = VOCAB[i];
      if (Math.abs(candidate.length - token.length) > limit) continue;

      var distance = editDistance(token, candidate, limit);
      if (distance > limit) continue;

      if (distance < best) {
        best = distance;
        matches = [candidate];
      } else if (distance === best) {
        matches.push(candidate);
      }
    }

    return matches.length ? matches : [token];
  }

  /**
   * Score every concept present in a message.
   *   phrase hit    3   several words agreed
   *   exact token   2
   *   corrected     1   spelling had to be repaired
   */
  function analyse(text) {
    var folded = fold(text);
    var tokens = tokenise(text);
    var scores = {};

    function add(concept, weight) {
      scores[concept] = (scores[concept] || 0) + weight;
    }

    var corrected = tokens.map(correct);
    var primary = corrected
      .map(function (options) {
        return options[0];
      })
      .join(' ');

    PHRASE_INDEX.forEach(function (entry) {
      if (folded.indexOf(entry.phrase) !== -1 || primary.indexOf(entry.phrase) !== -1) {
        add(entry.concept, 3);
        return;
      }

      // Try alternative spellings before giving up on a phrase.
      var alternates = corrected.reduce(
        function (acc, options) {
          var next = [];
          acc.forEach(function (prefix) {
            options.slice(0, 2).forEach(function (option) {
              next.push(prefix ? prefix + ' ' + option : option);
            });
          });
          return next.slice(0, 16);
        },
        ['']
      );

      for (var i = 0; i < alternates.length; i++) {
        if (alternates[i].indexOf(entry.phrase) !== -1) {
          add(entry.concept, 3);
          return;
        }
      }
    });

    tokens.forEach(function (token, index) {
      if (TOKEN_INDEX[token]) {
        TOKEN_INDEX[token].forEach(function (concept) {
          add(concept, 2);
        });
        return;
      }

      corrected[index].forEach(function (candidate) {
        if (!TOKEN_INDEX[candidate]) return;
        TOKEN_INDEX[candidate].forEach(function (concept) {
          add(concept, 1);
        });
      });
    });

    return {
      raw: text,
      folded: folded,
      tokens: tokens,
      scores: scores,
      has: function (concept) {
        return (scores[concept] || 0) > 0;
      },
      strongest: function (candidates) {
        var winner = null;
        var top = 0;
        candidates.forEach(function (concept) {
          var value = scores[concept] || 0;
          if (value > top) {
            top = value;
            winner = concept;
          }
        });
        return winner;
      }
    };
  }

  /* ==========================================================
     4. LANGUAGE DETECTION AND BILINGUAL OUTPUT
     Preserved from V12: NOVA answers in the language it was
     addressed in, and remembers the choice.
     ========================================================== */

  var ROMAN_URDU_MARKERS = [
    'aap', 'ap', 'hai', 'hain', 'kya', 'kia', 'mujhe', 'mujhy', 'mera', 'meri',
    'chahiye', 'chahye', 'karo', 'kro', 'kaise', 'kesay', 'kitna', 'kitne',
    'nahi', 'nai', 'haan', 'han', 'acha', 'shadi', 'shaadi', 'dulha', 'baraat',
    'mehndi', 'walima', 'nikkah', 'pehnu', 'dikhao', 'batao', 'thora', 'zyada',
    'bohat', 'shahi', 'saada', 'din', 'hafta', 'mahina', 'ke', 'ka', 'ki',
    'ko', 'se', 'mein', 'men', 'liye', 'wala', 'walay', 'hoon', 'hun'
  ];

  function detectLanguage(text) {
    var tokens = fold(text).split(' ').filter(Boolean);
    if (!tokens.length) return null;

    var hits = 0;
    tokens.forEach(function (token) {
      if (ROMAN_URDU_MARKERS.indexOf(token) !== -1) hits += 1;
    });

    // A single incidental word is not enough to switch language.
    if (hits >= 2 || (hits === 1 && tokens.length <= 3)) return 'roman';
    return 'english';
  }

  function isRoman() {
    return profile.language === 'roman';
  }

  /** Pick the right language. Every user-facing string goes through this. */
  function say(english, roman) {
    return isRoman() && roman ? roman : english;
  }

  /* ==========================================================
     5. PROFILE AND PERSISTENT HISTORY
     The V12 profile shape, unchanged, so a returning client's
     stored brief still loads.
     ========================================================== */

  function blankProfile() {
    return {
      version: 14,
      name: null,
      language: null,

      country: null,
      locationType: null,

      occasion: null,
      event: null,
      role: null,

      eventDate: null,
      daysRemaining: null,
      timelineText: null,

      outfitType: null,
      genderDirection: null,

      budgetMin: null,
      budgetMax: null,
      budgetText: null,

      colour: null,
      avoidColour: null,

      workLevel: null,
      style: null,
      fit: null,

      height: null,
      weight: null,
      complexion: null,
      size: null,
      fabric: null,

      needsRecommendation: false,
      deliveryWarningGiven: false,

      shownHandles: [],

      lastQuestion: null,
      lastIntent: null,
      conversationStage: 'discovery',

      notifyPromptedAt: null,
      updatedAt: null
    };
  }

  function loadJSON(key, fallback) {
    try {
      var data = JSON.parse(window.localStorage.getItem(key));
      return data || fallback;
    } catch (e) {
      return fallback;
    }
  }

  var stored = loadJSON(STORAGE_KEY, {});
  var profile = Object.assign(blankProfile(), stored && stored.version === 14 ? stored : {});
  var history = loadJSON(HISTORY_KEY, []);
  if (!Array.isArray(history)) history = [];

  function saveState() {
    try {
      profile.updatedAt = Date.now();
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(profile));
      window.localStorage.setItem(HISTORY_KEY, JSON.stringify(history.slice(-HISTORY_LIMIT)));
    } catch (e) {
      /* Private browsing or a full quota. NOVA still works; it
         simply forgets between sessions. */
    }
  }

  function remember(role, content) {
    if (!content) return;
    history.push({ role: role, content: content, time: Date.now() });
    if (history.length > HISTORY_LIMIT) history = history.slice(-HISTORY_LIMIT);
  }

  function resetProfile() {
    profile = blankProfile();
    history = [];
    try {
      window.localStorage.removeItem(STORAGE_KEY);
      window.localStorage.removeItem(HISTORY_KEY);
    } catch (e) {}
  }

  /* ==========================================================
     6. FACT EXTRACTION
     Every field V12 could learn, now learned through the
     tolerant matcher rather than exact string containment.
     ========================================================== */

  var GARMENT_LABELS = {
    sherwani: 'Sherwani',
    princeCoat: 'Prince Coat',
    suit: 'Suit',
    kurta: 'Kurta / Traditional',
    openJacket: 'Open Jacket',
    waistcoat: 'Waistcoat'
  };

  var EVENT_LABELS = {
    baraat: 'Baraat',
    walima: 'Walima',
    nikkah: 'Nikkah',
    mehndi: 'Mehndi',
    engagement: 'Engagement'
  };

  var COLOUR_LABELS = {
    ivory: 'ivory and cream',
    black: 'black and deep tones',
    gold: 'gold and warm metallics',
    pastel: 'soft pastels',
    jewel: 'jewel tones'
  };

  var COMPLEXION_LABELS = {
    complexionLight: 'Fair / Light',
    complexionMedium: 'Medium',
    complexionWheatish: 'Wheatish / Warm',
    complexionDeep: 'Deep'
  };

  /* ------------------------------------------------------------------
     HOUSE DIRECTION.

     Ahsan Tariq Couture is a menswear and groomwear house; the
     womenswear collection exists but is not the default reading of an
     unqualified brief. Previously genderDirection was declared on the
     profile and never populated, and scoreProduct applied no direction
     signal at all — so on an empty brief every available product
     scored exactly 1 (the availability point) and the ranking was
     effectively arbitrary. That is why NOVA could open on womenswear.

     Direction is now inferred from what the client actually says, and
     falls back to menswear rather than to chance. An explicit
     womenswear request still switches it immediately.
     ------------------------------------------------------------------ */

  var WOMENS_HINTS = [
    'women', 'woman', 'womens', 'womenswear', 'ladies', 'lady', 'girl',
    'bride', 'bridal', 'dulhan', 'lehenga', 'lehnga', 'saree', 'sari',
    'gharara', 'sharara', 'maxi', 'frock', 'anarkali', 'pishwas',
    'blouse', 'choli', 'gown', 'dupatta'
  ];

  var MENS_HINTS = [
    'men', 'man', 'mens', 'menswear', 'groom', 'dulha', 'gents',
    'sherwani', 'prince coat', 'princecoat', 'waistcoat', 'kurta',
    'shalwar', 'achkan', 'bandhgala', 'tuxedo', 'suit', 'baraat'
  ];

  /* Whole-word matching is essential here, not substring matching:
     "womenswear" contains "menswear", and "women" contains "men", so a
     naive indexOf scores an explicit womenswear request equally for
     both directions and resolves to a tie. fold() has already reduced
     punctuation to spaces, so \b behaves predictably. */
  var HINT_RE = {};

  function hintHits(folded, hints) {
    var count = 0;

    for (var i = 0; i < hints.length; i++) {
      var hint = fold(hints[i]);
      if (!hint) continue;

      var re = HINT_RE[hint];
      if (!re) {
        re = new RegExp('\\b' + hint.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '\\b');
        HINT_RE[hint] = re;
      }

      if (re.test(folded)) count++;
    }

    return count;
  }

  function resolveDirection(folded) {
    if (!folded) return null;
    var womens = hintHits(folded, WOMENS_HINTS);
    var mens = hintHits(folded, MENS_HINTS);
    if (womens > mens) return 'womens';
    if (mens > womens) return 'mens';
    return null;
  }

  function detectDirection(text) {
    return resolveDirection(fold(text || ''));
  }

  /** Which direction does a catalogue item read as? null = unmarked. */
  function productDirection(product) {
    /* Collection membership is authoritative: it is what the house
       actually merchandised. Keywords are only the fallback. */
    if (product.direction) return product.direction;
    return resolveDirection(product.searchText || '');
  }

  function activeDirection() {
    // The house default is menswear. It is a default, not a lock:
    // one womenswear word in the brief overrides it.
    return profile.genderDirection || 'mens';
  }

  function extractFacts(reading) {
    // --- direction ----------------------------------------------
    var direction = detectDirection(reading.raw || '');
    if (direction) profile.genderDirection = direction;

    // --- occasion and role -------------------------------------
    if (reading.has('ownWedding')) {
      profile.occasion = 'Wedding';
      profile.role = 'Groom';
    } else if (reading.has('guestWedding')) {
      profile.occasion = 'Wedding';
      if (reading.has('brother')) profile.role = 'Brother of Groom/Bride';
      else profile.role = 'Wedding Guest';
    } else if (reading.has('wedding')) {
      profile.occasion = 'Wedding';
    }

    if (reading.has('groom') && !profile.role) {
      profile.occasion = profile.occasion || 'Wedding';
      profile.role = 'Groom';
    }

    if (reading.has('engagement')) {
      profile.occasion = 'Engagement';
      profile.event = 'Engagement';
    }
    if (reading.has('party')) profile.occasion = profile.occasion || 'Formal Evening';
    if (reading.has('eid')) profile.occasion = profile.occasion || 'Eid';

    // --- wedding event ------------------------------------------
    var event = reading.strongest(['baraat', 'walima', 'nikkah', 'mehndi']);
    if (event) {
      profile.occasion = 'Wedding';
      profile.event = EVENT_LABELS[event];
    }

    // --- garment -------------------------------------------------
    var garment = reading.strongest(['sherwani', 'princeCoat', 'suit', 'kurta', 'openJacket', 'waistcoat']);
    if (garment) profile.outfitType = GARMENT_LABELS[garment];

    // --- embellishment weight ------------------------------------
    var weight = reading.strongest(['light', 'heavy', 'medium']);
    if (weight === 'light') profile.workLevel = 'Light / Refined';
    else if (weight === 'heavy') profile.workLevel = 'Heavy';
    else if (weight === 'medium') profile.workLevel = 'Medium';

    // --- style. V12 allowed combinations, e.g. "Royal + Modern". --
    var styles = [];
    if (reading.has('royal')) styles.push('Royal');
    if (reading.has('classy')) styles.push('Classy');
    if (reading.has('modern')) styles.push('Modern');
    if (reading.has('traditional')) styles.push('Traditional');
    if (styles.length) profile.style = styles.join(' + ');

    // --- colour ---------------------------------------------------
    var colour = reading.strongest(['ivory', 'black', 'gold', 'pastel', 'jewel']);
    if (colour) profile.colour = COLOUR_LABELS[colour];

    // --- complexion -----------------------------------------------
    var complexion = reading.strongest([
      'complexionLight', 'complexionMedium', 'complexionWheatish', 'complexionDeep'
    ]);
    if (complexion) profile.complexion = COMPLEXION_LABELS[complexion];

    // --- fit ------------------------------------------------------
    if (reading.has('slim')) profile.fit = 'Slim';
    else if (reading.has('regular')) profile.fit = 'Regular';

    // --- delivery region ------------------------------------------
    if (reading.has('international')) profile.locationType = 'International';
    else if (reading.has('pakistan')) profile.locationType = 'Pakistan';

    // --- measurements ---------------------------------------------
    extractMeasurements(reading.raw);

    // --- timeline and budget --------------------------------------
    parseTimeline(reading.raw);
    parseBudget(reading.raw);
  }

  /**
   * Height and weight from free text.
   * Handles 5'11", 5 ft 11, 180cm, 75kg, 165 lbs.
   */
  function extractMeasurements(text) {
    var raw = String(text || '').toLowerCase();

    var feetInches = raw.match(/(\d)\s*(?:'|ft|feet|foot)\s*(\d{1,2})?/);
    if (feetInches) {
      profile.height = feetInches[1] + "'" + (feetInches[2] || '0') + '"';
    } else {
      var cm = raw.match(/(\d{2,3})\s*cm\b/);
      if (cm && Number(cm[1]) >= 120 && Number(cm[1]) <= 230) profile.height = cm[1] + ' cm';
    }

    var kg = raw.match(/(\d{2,3})\s*(?:kg|kilo|kilos|kgs)\b/);
    if (kg && Number(kg[1]) >= 30 && Number(kg[1]) <= 250) {
      profile.weight = kg[1] + ' kg';
    } else {
      var lbs = raw.match(/(\d{2,3})\s*(?:lb|lbs|pounds)\b/);
      if (lbs) profile.weight = lbs[1] + ' lbs';
    }

    var size = raw.match(/\b(?:size)\s*([smlx]{1,4}|\d{2})\b/);
    if (size) profile.size = size[1].toUpperCase();
  }

  /* ==========================================================
     7. TIMELINE
     ========================================================== */

  function parseTimeline(text) {
    var t = tokenise(text).join(' ');
    var match;

    match = t.match(/(\d{1,3})\s*days?/);
    if (match) {
      profile.daysRemaining = Number(match[1]);
      profile.timelineText = match[1] + ' days';
      return;
    }

    match = t.match(/(\d{1,2})\s*weeks?/);
    if (match) {
      profile.daysRemaining = Number(match[1]) * 7;
      profile.timelineText = match[1] + ' weeks';
      return;
    }

    match = t.match(/(\d{1,2})\s*months?/);
    if (match) {
      profile.daysRemaining = Number(match[1]) * 30;
      profile.timelineText = match[1] + ' months';
      return;
    }

    if (/next week/.test(t)) {
      profile.daysRemaining = 7;
      profile.timelineText = 'about 1 week';
      return;
    }

    if (/next month/.test(t)) {
      profile.daysRemaining = 30;
      profile.timelineText = 'about 1 month';
      return;
    }

    if (/tomorrow/.test(t)) {
      profile.daysRemaining = 1;
      profile.timelineText = 'tomorrow';
      return;
    }

    // An explicit date: "12 March", "2026-03-12", "12/03/2026".
    var date = parseEventDate(text);
    if (date) {
      var days = Math.ceil((date - Date.now()) / 86400000);
      if (days >= 0 && days < 900) {
        profile.eventDate = new Date(date).toISOString().slice(0, 10);
        profile.daysRemaining = days;
        profile.timelineText = days + ' days (' + profile.eventDate + ')';
      }
    }
  }

  function parseEventDate(text) {
    var raw = String(text || '');

    var iso = raw.match(/\b(20\d{2})[-/](\d{1,2})[-/](\d{1,2})\b/);
    if (iso) {
      var d1 = new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]));
      return isNaN(d1) ? null : d1.getTime();
    }

    var months = ['january', 'february', 'march', 'april', 'may', 'june',
      'july', 'august', 'september', 'october', 'november', 'december'];
    var named = fold(raw).match(/\b(\d{1,2})\s+([a-z]{3,9})\b/);

    if (named) {
      var idx = -1;
      for (var i = 0; i < months.length; i++) {
        if (months[i].indexOf(named[2]) === 0) { idx = i; break; }
      }
      if (idx >= 0) {
        var now = new Date();
        var candidate = new Date(now.getFullYear(), idx, Number(named[1]));
        // A date already past this year means next year.
        if (candidate.getTime() < now.getTime() - 86400000) {
          candidate = new Date(now.getFullYear() + 1, idx, Number(named[1]));
        }
        return candidate.getTime();
      }
    }

    return null;
  }

  /* ==========================================================
     8. BUDGET
     ========================================================== */

  function parseMoney(value) {
    var v = String(value || '').toLowerCase().replace(/[,\s]/g, '');
    var multiplier = 1;

    if (/lac|lakh/.test(v)) multiplier = 100000;
    else if (/k$|thousand|hazar/.test(v)) multiplier = 1000;
    else if (/m$|million/.test(v)) multiplier = 1000000;

    var number = parseFloat(v.replace(/[^\d.]/g, ''));
    if (isNaN(number) || number <= 0) return null;

    var result = number * multiplier;
    // A bare number below 1,000 is not a couture budget.
    return result >= 1000 ? result : null;
  }

  function parseBudget(text) {
    var raw = fold(text);

    if (/\bflexible\b|\bno limit\b|\bopen budget\b/.test(raw)) {
      profile.budgetMin = null;
      profile.budgetMax = null;
      profile.budgetText = 'Flexible';
      return;
    }

    // Ranges: "100k to 150k", "100k-150k", "between 1 and 2 lac"
    var range = raw.match(/(\d+(?:\.\d+)?\s*(?:k|lac|lakh|thousand|hazar|m)?)\s*(?:to|-|and|se)\s*(\d+(?:\.\d+)?\s*(?:k|lac|lakh|thousand|hazar|m)?)/);
    if (range) {
      var lo = parseMoney(range[1]);
      var hi = parseMoney(range[2]);
      if (lo && hi) {
        profile.budgetMin = Math.min(lo, hi);
        profile.budgetMax = Math.max(lo, hi);
        profile.budgetText = formatMoneyRange(profile.budgetMin, profile.budgetMax);
        return;
      }
    }

    // "under 100k" / "below 2 lac" / "up to 150k"
    var under = raw.match(/(?:under|below|less than|up to|max|maximum|tak)\s*(\d+(?:\.\d+)?\s*(?:k|lac|lakh|thousand|hazar|m)?)/);
    if (under) {
      var cap = parseMoney(under[1]);
      if (cap) {
        profile.budgetMin = null;
        profile.budgetMax = cap;
        profile.budgetText = 'Under ' + moneyFromMajor(cap);
        return;
      }
    }

    // "250k+" / "above 2 lac"
    var over = raw.match(/(?:above|over|more than|plus)\s*(\d+(?:\.\d+)?\s*(?:k|lac|lakh|thousand|hazar|m)?)|(\d+(?:\.\d+)?\s*(?:k|lac|lakh|thousand|hazar|m)?)\s*\+/);
    if (over) {
      var floorValue = parseMoney(over[1] || over[2]);
      if (floorValue) {
        profile.budgetMin = floorValue;
        profile.budgetMax = null;
        profile.budgetText = moneyFromMajor(floorValue) + '+';
        return;
      }
    }

    // A single figure, only when the message is actually about money.
    if (/budget|price|cost|k\b|lac|lakh|thousand|hazar/.test(raw)) {
      var single = raw.match(/(\d+(?:\.\d+)?\s*(?:k|lac|lakh|thousand|hazar|m)?)/);
      if (single) {
        var value = parseMoney(single[1]);
        if (value) {
          profile.budgetMin = null;
          profile.budgetMax = value;
          profile.budgetText = 'Around ' + moneyFromMajor(value);
        }
      }
    }
  }

  function formatMoneyRange(lo, hi) {
    return moneyFromMajor(lo) + ' – ' + moneyFromMajor(hi);
  }

  /** Format an amount expressed in major units (not cents). */
  function moneyFromMajor(amount) {
    if (window.AURUM && typeof window.AURUM.formatMoney === 'function') {
      return window.AURUM.formatMoney(Math.round(amount * 100));
    }
    return String(Math.round(amount)).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  /* ==========================================================
     9. DELIVERY ASSESSMENT
     V12 wording preserved. Every window is described as
     requiring Design Team confirmation — NOVA never commits the
     atelier to a date it cannot verify.
     ========================================================== */

  function deliveryAssessment() {
    if (!profile.daysRemaining) return null;

    var days = profile.daysRemaining;

    if (profile.locationType === 'International') {
      if (days < 25) {
        return say(
          'That timeline is shorter than our international express window. I would want the Design Team to confirm feasibility before promising it.',
          'International order ke liye yeh timeline hamari express window se bhi short hai. Main is commitment ko guess nahi karunga — Design Team se availability confirm karni hogi.'
        );
      }
      if (days <= 40) {
        return say(
          'That falls within our 25–40 day international express window, subject to Design Team confirmation.',
          'Yeh hamari international express window 25–40 days ke andar hai, lekin exact feasibility Design Team confirm karegi.'
        );
      }
      return say(
        'The timeline looks comfortable. Our international express window is approximately 25–40 days.',
        'Timeline comfortable lag rahi hai. International orders ke liye express window 25–40 days hoti hai.'
      );
    }

    if (profile.locationType === 'Pakistan') {
      if (days < 15) {
        return say(
          'That is shorter than our 15–25 day Pakistan express window, so the Design Team would need to confirm feasibility.',
          'Pakistan ke liye bhi yeh hamari 15–25 day express window se short hai. Design Team ko feasibility confirm karni hogi.'
        );
      }
      if (days <= 25) {
        return say(
          'That falls within our 15–25 day Pakistan express window, subject to Design Team confirmation.',
          'Yeh Pakistan ki 15–25 day express window ke andar hai. Exact feasibility Design Team confirm karegi.'
        );
      }
      return say(
        'The timeline is comfortable. Pakistan express preparation is approximately 15–25 days.',
        'Timeline comfortable hai. Pakistan ke liye express preparation approximately 15–25 days hoti hai.'
      );
    }

    return null;
  }

  /* ==========================================================
     10. CATALOGUE
     ========================================================== */

  var CATALOGUE_KEY = 'at_nova_catalogue_v14';
  var CATALOGUE_TTL = 30 * 60 * 1000;
  var cataloguePromise = null;

  function normaliseProduct(raw) {
    var variants = Array.isArray(raw.variants) ? raw.variants : [];
    var available = variants.some(function (v) { return v.available; });

    var prices = variants
      .map(function (v) { return parseFloat(v.price); })
      .filter(function (n) { return !isNaN(n); });

    var image = '';
    if (Array.isArray(raw.images) && raw.images.length) image = raw.images[0].src;
    else if (raw.featured_image) image = raw.featured_image;

    var firstAvailable = variants.filter(function (v) { return v.available; })[0];

    return {
      handle: raw.handle || '',
      title: raw.title || '',
      url: raw.handle ? '/products/' + raw.handle : '',
      image: image,
      price: prices.length ? Math.min.apply(null, prices) : null,
      available: available,
      variantId: firstAvailable ? firstAvailable.id : (variants[0] && variants[0].id) || null,
      searchText: fold([raw.title, raw.product_type, (raw.tags || []).join(' ')].join(' '))
    };
  }

  /* ------------------------------------------------------------------
     COLLECTION-DERIVED DIRECTION

     The house names pieces poetically — "Amara Drape Ensemble", "Ivory
     Radiance", "Noir Luxe Ensemble" — so no gendered word appears in
     the title, type or tags. Keyword inference therefore returned null
     for genuine womenswear, the piece was treated as neutral, and a
     neutral piece is shown to everyone. That is how a client who said
     "I am the groom" was shown ladies' pieces.

     The store already holds the answer: those products sit in the
     womenswear collection. Read that once and treat membership as
     ground truth, falling back to keywords only for products in
     neither list. Failures are swallowed — an unreachable collection
     must degrade to the old behaviour, never break the recommendation.
     ------------------------------------------------------------------ */
  var directionIndex = null;

  function loadDirectionIndex() {
    if (directionIndex) return directionIndex;

    function handlesIn(collection) {
      return fetch('/collections/' + collection + '/products.json?limit=250', {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' }
      })
        .then(function (response) { return response.ok ? response.json() : { products: [] }; })
        .then(function (data) {
          return (data.products || []).map(function (p) { return p.handle; });
        })
        .catch(function () { return []; });
    }

    directionIndex = Promise.all([
      handlesIn('womenswear'),
      handlesIn('menswear-pakistan')
    ])
      .then(function (sets) {
        var index = {};
        sets[0].forEach(function (h) { index[h] = 'womens'; });
        sets[1].forEach(function (h) { index[h] = 'mens'; });
        return index;
      })
      .catch(function () { return {}; });

    return directionIndex;
  }

  function fetchCatalogue() {
    if (cataloguePromise) return cataloguePromise;

    try {
      var cached = JSON.parse(window.sessionStorage.getItem(CATALOGUE_KEY));
      if (cached && cached.at && Date.now() - cached.at < CATALOGUE_TTL && cached.items.length) {
        cataloguePromise = Promise.resolve(cached.items);
        return cataloguePromise;
      }
    } catch (e) {}

    cataloguePromise = (function () {
      var collected = [];
      var seen = {};

      function page(number) {
        return fetch('/products.json?limit=250&page=' + number, {
          credentials: 'same-origin',
          headers: { Accept: 'application/json' }
        })
          .then(function (response) {
            if (!response.ok) throw new Error('catalogue');
            return response.json();
          })
          .then(function (data) {
            var batch = Array.isArray(data.products) ? data.products : [];

            batch.forEach(function (raw) {
              var product = normaliseProduct(raw);
              if (!product.handle || seen[product.handle]) return;
              seen[product.handle] = true;
              collected.push(product);
            });

            if (batch.length === 250 && number < 6) return page(number + 1);
            return collected;
          });
      }

      return page(1)
        .then(function (items) {
          /* Stamp each product with its collection-derived direction
             before caching, so scoring never has to guess. */
          return loadDirectionIndex().then(function (index) {
            items.forEach(function (product) {
              if (index[product.handle]) product.direction = index[product.handle];
            });
            return items;
          });
        })
        .then(function (items) {
          try {
            window.sessionStorage.setItem(CATALOGUE_KEY, JSON.stringify({ at: Date.now(), items: items }));
          } catch (e) {}
          return items;
        })
        .catch(function () {
          return [];
        });
    })();

    return cataloguePromise;
  }

  /** The search terms implied by the current brief. */
  function recommendationTerms() {
    var terms = [];

    if (profile.outfitType) terms.push(profile.outfitType);
    if (profile.event) terms.push(profile.event);
    if (profile.occasion && profile.occasion !== 'Wedding') terms.push(profile.occasion);
    if (profile.style) terms.push(profile.style.split(' + ')[0]);
    if (profile.colour) terms.push(profile.colour.split(' ')[0]);

    return terms.filter(Boolean);
  }

  function scoreProduct(product) {
    var score = 0;
    var text = product.searchText;

    function hit(term) {
      return term && text.indexOf(fold(term)) !== -1;
    }

    if (profile.outfitType) {
      if (hit(profile.outfitType)) score += 6;
      if (profile.outfitType === 'Prince Coat' && hit('coat')) score += 3;
      if (profile.outfitType === 'Open Jacket' && hit('jacket')) score += 3;
      if (profile.outfitType === 'Kurta / Traditional' && hit('kurta')) score += 4;
    }

    if (profile.event) {
      if (hit(profile.event)) score += 4;
      if (profile.event === 'Baraat' && hit('wedding')) score += 2;
      if (profile.event === 'Walima' && hit('reception')) score += 2;
    }

    if (profile.occasion && hit(profile.occasion)) score += 2;
    if (profile.style) {
      profile.style.split(' + ').forEach(function (s) { if (hit(s)) score += 2; });
    }
    if (profile.workLevel && hit(profile.workLevel.split(' ')[0])) score += 1;
    if (profile.colour && hit(profile.colour.split(' ')[0])) score += 2;
    if (profile.role === 'Groom' && (hit('groom') || hit('menswear'))) score += 2;

    // Budget is a preference, not a filter: showing nothing is a
    // worse answer than showing the closest pieces.
    if (product.price !== null) {
      if (profile.budgetMax && product.price <= profile.budgetMax) score += 3;
      else if (profile.budgetMax && product.price <= profile.budgetMax * 1.25) score += 1;
      else if (profile.budgetMax) score -= 2;

      if (profile.budgetMin && product.price >= profile.budgetMin) score += 1;
    }

    /* Direction. A piece from the opposite direction is pushed below
       the qualifying threshold rather than merely demoted, so it can
       never lead the opening set. Unmarked pieces stay neutral — many
       genuinely are, and excluding them would thin the catalogue. */
    var wanted = activeDirection();
    var pd = productDirection(product);
    if (pd) {
      if (pd === wanted) score += 2;
      else score -= 12;
    }

    if (product.available) score += 1;
    if (profile.shownHandles.indexOf(product.handle) !== -1) score -= 8;

    return score;
  }

  function rankProducts(limit) {
    return fetchCatalogue().then(function (items) {
      if (!items.length) return [];

      var ranked = items
        .map(function (product) { return { product: product, score: scoreProduct(product) }; })
        .filter(function (entry) { return entry.score > 0; })
        .sort(function (a, b) { return b.score - a.score; })
        .slice(0, limit || MAX_PRODUCTS)
        .map(function (entry) { return entry.product; });

      ranked.forEach(function (product) {
        if (profile.shownHandles.indexOf(product.handle) === -1) {
          profile.shownHandles.push(product.handle);
        }
      });

      if (profile.shownHandles.length > 40) {
        profile.shownHandles = profile.shownHandles.slice(-40);
      }

      saveState();
      return ranked;
    });
  }

  function searchCatalogue(query, limit) {
    var terms = tokenise(query).filter(function (t) { return t.length > 2; });
    if (!terms.length) return Promise.resolve([]);

    return fetchCatalogue().then(function (items) {
      return items
        .map(function (product) {
          var score = 0;
          terms.forEach(function (term) {
            if (product.searchText.indexOf(term) !== -1) score += 2;
          });

          /* Same direction rule as the ranked path, so the fallback
             search cannot reintroduce what ranking just excluded. */
          var pd = productDirection(product);
          if (pd && pd !== activeDirection()) score -= 12;

          if (product.available) score += 1;
          return { product: product, score: score };
        })
        .filter(function (entry) { return entry.score > 1; })
        .sort(function (a, b) { return b.score - a.score; })
        .slice(0, limit || MAX_PRODUCTS)
        .map(function (entry) { return entry.product; });
    });
  }

  /* ==========================================================
     11. RECOMMENDATION REASONING
     V12's direction and colour logic, preserved.
     ========================================================== */

  function chooseOutfitDirection() {
    if (profile.outfitType) return profile.outfitType;

    if (profile.role === 'Groom') {
      if (profile.event === 'Baraat') return 'Sherwani';
      if (profile.event === 'Nikkah') return 'Sherwani or refined Prince Coat';
      if (profile.event === 'Walima') return 'Prince Coat or formal Suit';
      if (profile.event === 'Mehndi') return 'Prince Coat or statement traditional separates';
    }

    if (profile.role === 'Wedding Guest' || profile.role === 'Brother of Groom/Bride') {
      if (profile.event === 'Baraat') return 'Prince Coat';
      if (profile.event === 'Walima') return 'Suit or Prince Coat';
      if (profile.event === 'Mehndi') return 'Kurta with a statement jacket';
      if (profile.event === 'Nikkah') return 'Refined Prince Coat or tailored traditional look';
    }

    if (profile.occasion === 'Engagement') return 'Prince Coat or tailored Suit';

    return 'a refined couture look';
  }

  function colourAdvice() {
    if (profile.colour && profile.colour !== 'NOVA Selection') return profile.colour;

    if (profile.event === 'Baraat') {
      if (profile.style && profile.style.indexOf('Royal') !== -1) {
        return 'ivory, antique gold, champagne or deeper regal tones';
      }
      return 'ivory, champagne or warm neutrals';
    }

    if (profile.event === 'Walima') return 'black, charcoal, ivory, silver-grey or restrained cool tones';
    if (profile.event === 'Mehndi') return 'olive, mustard, rust, emerald or richer festive colour';
    if (profile.event === 'Nikkah') return 'ivory, cream, soft beige or muted pastels';

    return 'a colour selected around your complexion and event';
  }

  /* ==========================================================
     12. VIEW
     ========================================================== */

  var view = {
    panel: root.querySelector('[data-nova-panel]'),
    chat: root.querySelector('[data-nova-chat]'),
    input: root.querySelector('[data-nova-input]'),
    form: root.querySelector('[data-nova-composer]'),
    typing: root.querySelector('[data-nova-typing]'),
    awake: root.querySelector('[data-nova-awake]'),
    notify: root.querySelector('[data-nova-notify]'),
    preview: root.querySelector('[data-nova-preview]'),
    previewImg: root.querySelector('[data-nova-preview-img]'),
    fileInput: root.querySelector('[data-nova-file]'),
    cameraInput: root.querySelector('[data-nova-camera]'),
    voiceButton: root.querySelector('[data-nova-voice]'),
    humanButton: root.querySelector('[data-nova-human]'),
    isOpen: false,
    trap: null
  };

  function element(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined) node.textContent = text;
    return node;
  }

  function scrollChatToEnd() {
    if (!view.chat) return;
    window.requestAnimationFrame(function () {
      view.chat.scrollTop = view.chat.scrollHeight;
    });
  }

  function prefersReducedMotion() {
    // Some embedded webviews (in-app browsers in particular) do not
    // expose matchMedia. Treating that as "no preference" keeps NOVA
    // working rather than throwing on the first reply.
    if (typeof window.matchMedia !== 'function') return false;
    try {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    } catch (e) {
      return false;
    }
  }

  /**
   * Render a message. textContent, never innerHTML — message text
   * is never treated as markup, so injected HTML cannot execute.
   */
  function bubble(text, who) {
    if (!view.chat) return null;

    var row = element('div', 'at-nova-row at-nova-row--' + (who || 'nova'));
    var node = element('div', 'at-nova-bubble at-nova-bubble--' + (who || 'nova'), text);

    row.appendChild(node);
    view.chat.appendChild(row);
    scrollChatToEnd();
    return node;
  }

  function clearChoices() {
    Array.prototype.slice.call(root.querySelectorAll('.at-nova-choices')).forEach(function (node) {
      if (node.parentNode) node.parentNode.removeChild(node);
    });
  }

  function renderChoices(choices) {
    if (!view.chat || !choices || !choices.length) return;

    var wrapper = element('div', 'at-nova-choices');
    wrapper.setAttribute('role', 'group');
    wrapper.setAttribute('aria-label', say('Suggested replies', 'Tajweez'));

    choices.forEach(function (choice) {
      var button = element('button', 'at-nova-choice', choice.label);
      button.type = 'button';
      button.dataset.novaChoice = choice.value || choice.label;
      wrapper.appendChild(button);
    });

    view.chat.appendChild(wrapper);
    scrollChatToEnd();
  }

  function showTyping(on) {
    if (!view.typing) return;
    view.typing.hidden = !on;
    view.typing.setAttribute('aria-hidden', String(!on));
    if (on) scrollChatToEnd();
  }

  /**
   * NOVA replies after a short, bounded pause so it reads as
   * considered rather than instantaneous, capped so it never
   * feels slow. Reduced motion removes the pause entirely.
   */
  function novaSay(text, choices) {
    remember('nova', text);
    saveState();

    if (prefersReducedMotion()) {
      bubble(text, 'nova');
      if (choices) renderChoices(choices);
      return Promise.resolve();
    }

    showTyping(true);
    var delay = Math.min(620, 240 + text.length * 4);

    return new Promise(function (resolve) {
      window.setTimeout(function () {
        showTyping(false);
        bubble(text, 'nova');
        if (choices) renderChoices(choices);
        resolve();
      }, delay);
    });
  }

  /**
   * Product card. VIEW PRODUCT is always present; ADD TO BAG only
   * when the variant is genuinely purchasable, because a disabled
   * primary action on a couture piece reads as a fault.
   */
  function renderProducts(products, heading) {
    if (!view.chat || !products.length) return;

    var wrapper = element('div', 'at-nova-products');

    if (heading) wrapper.appendChild(element('p', 'at-nova-products__heading', heading));

    products.forEach(function (product) {
      var card = element('article', 'at-nova-product');

      if (product.image) {
        var figure = element('a', 'at-nova-product__media');
        figure.href = product.url;
        figure.tabIndex = -1;
        figure.setAttribute('aria-hidden', 'true');

        var img = document.createElement('img');
        img.src = product.image.replace(/(\.[a-z]+)(\?.*)?$/i, '_240x$1$2');
        img.alt = '';
        img.loading = 'lazy';
        img.width = 96;
        img.height = 120;

        figure.appendChild(img);
        card.appendChild(figure);
      }

      var body = element('div', 'at-nova-product__body');

      var title = element('h4', 'at-nova-product__title');
      var link = element('a', null, product.title);
      link.href = product.url;
      title.appendChild(link);
      body.appendChild(title);

      if (product.price !== null) {
        body.appendChild(element('p', 'at-nova-product__price', moneyFromMajor(product.price)));
      }

      if (!product.available) {
        body.appendChild(element('p', 'at-nova-product__status', say('Made to order', 'Order par tayar')));
      }

      var actions = element('div', 'at-nova-product__actions');

      var viewLink = element('a', 'at-nova-btn at-nova-btn--primary', say('View product', 'Product dekhein'));
      viewLink.href = product.url;
      actions.appendChild(viewLink);

      if (product.available && product.variantId) {
        var add = element('button', 'at-nova-btn at-nova-btn--ghost', say('Add to bag', 'Bag mein daalein'));
        add.type = 'button';
        add.dataset.novaAdd = product.variantId;
        add.dataset.novaAddTitle = product.title;
        actions.appendChild(add);
      }

      body.appendChild(actions);
      card.appendChild(body);
      wrapper.appendChild(card);
    });

    view.chat.appendChild(wrapper);
    scrollChatToEnd();
  }

  /** Replay stored conversation so a returning client keeps context. */
  function restoreHistory() {
    if (!view.chat || !history.length) return false;

    view.chat.innerHTML = '';

    history.slice(-HISTORY_REPLAY).forEach(function (message) {
      if (!message || !message.content) return;
      bubble(message.content, message.role === 'user' ? 'user' : 'nova');
    });

    var marker = element('p', 'at-nova-resume', say('Continuing where we left off.', 'Wahin se aage badhte hain.'));
    view.chat.appendChild(marker);
    scrollChatToEnd();
    return true;
  }

  /* ==========================================================
     13. DISCOVERY FLOW
     V12's flow, intact: ask only what is still missing.
     ========================================================== */

  function nextQuestion() {
    /* ---- wedding role ---- */
    if (profile.occasion === 'Wedding' && !profile.role) {
      profile.lastQuestion = 'role';
      saveState();
      novaSay(
        say(
          'Perfect. Is this for your own wedding, or are you dressing for someone else’s wedding?',
          'Perfect. Aap apni wedding ke liye dekh rahe hain ya kisi aur ki wedding attend karni hai?'
        ),
        [
          { label: say('My wedding', 'Meri wedding'), value: 'my wedding' },
          { label: say("Someone else's wedding", 'Kisi aur ki wedding'), value: 'someone else wedding' }
        ]
      );
      return true;
    }

    /* ---- wedding event ---- */
    if (profile.occasion === 'Wedding' && !profile.event) {
      profile.lastQuestion = 'event';
      saveState();
      novaSay(
        profile.role === 'Groom'
          ? say('Great. Which event are we building the look for?', 'Great. Kis event ke liye look build karna hai?')
          : say('Which wedding event are you dressing for?', 'Kis wedding event ke liye outfit chahiye?'),
        [
          { label: 'Nikkah', value: 'nikkah' },
          { label: 'Mehndi', value: 'mehndi' },
          { label: 'Baraat', value: 'baraat' },
          { label: 'Walima', value: 'walima' }
        ]
      );
      return true;
    }

    /* ---- timeline ---- */
    if ((profile.occasion || profile.event) && !profile.daysRemaining) {
      profile.lastQuestion = 'timeline';
      saveState();
      novaSay(
        say(
          'How much time do we have before the event? An estimate is perfectly fine.',
          'Event mein kitna time reh gaya hai? Approximate bhi chalega.'
        ),
        [
          { label: '< 15 days', value: '10 days' },
          { label: '15–25 days', value: '20 days' },
          { label: '1 month', value: '1 month' },
          { label: '2+ months', value: '2 months' }
        ]
      );
      return true;
    }

    /* ---- delivery location ---- */
    if (profile.daysRemaining && !profile.locationType) {
      profile.lastQuestion = 'location';
      saveState();
      novaSay(
        say(
          'Will the outfit be required in Pakistan or internationally?',
          'Outfit Pakistan mein chahiye ya international delivery hogi?'
        ),
        [
          { label: 'Pakistan', value: 'Pakistan' },
          { label: 'International', value: 'International' }
        ]
      );
      return true;
    }

    /* ---- delivery assessment (stated once) ---- */
    if (profile.daysRemaining && profile.locationType && !profile.deliveryWarningGiven) {
      var assessment = deliveryAssessment();
      profile.deliveryWarningGiven = true;
      saveState();
      if (assessment) novaSay(assessment);
    }

    /* ---- outfit direction ---- */
    if (profile.occasion && !profile.outfitType && !profile.needsRecommendation) {
      profile.lastQuestion = 'outfit';
      saveState();
      novaSay(
        say(
          'Do you already have an outfit direction in mind, or would you like me to choose the direction for you?',
          'Aapke mind mein koi specific outfit hai, ya main event ke hisaab se direction suggest karun?'
        ),
        [
          { label: 'Sherwani', value: 'sherwani' },
          { label: 'Prince Coat', value: 'prince coat' },
          { label: 'Suit', value: 'suit' },
          { label: say('Suggest for me', 'Aap suggest karein'), value: 'suggest for me' }
        ]
      );
      return true;
    }

    /* ---- style ---- */
    if (!profile.style) {
      profile.lastQuestion = 'style';
      saveState();
      novaSay(
        say('What kind of presence do you want the look to have?', 'Look ka mood kaisa chahiye?'),
        [
          { label: 'Royal', value: 'royal' },
          { label: 'Classy', value: 'classy' },
          { label: 'Modern', value: 'modern' },
          { label: 'Traditional', value: 'traditional' }
        ]
      );
      return true;
    }

    /* ---- embellishment ---- */
    if (!profile.workLevel) {
      profile.lastQuestion = 'work';
      saveState();
      novaSay(
        say(
          'How much embellishment would you prefer — refined and light, balanced, or properly heavy?',
          'Embellishment kitni rakhni hai — clean/light, balanced ya properly heavy?'
        ),
        [
          { label: say('Light / Refined', 'Light / Clean'), value: 'light minimal' },
          { label: 'Medium', value: 'medium' },
          { label: 'Heavy', value: 'heavy' }
        ]
      );
      return true;
    }

    /* ---- colour ---- */
    if (!profile.colour) {
      profile.lastQuestion = 'colour';
      saveState();
      novaSay(
        say('Do you have a colour family in mind?', 'Colour family ka koi preference hai?'),
        [
          { label: 'Ivory / Cream', value: 'ivory' },
          { label: 'Black / Deep', value: 'black' },
          { label: 'Gold / Warm', value: 'gold' },
          { label: 'Pastels', value: 'pastel' },
          { label: say('Choose for me', 'Aap choose karein'), value: 'choose colour for me' }
        ]
      );
      return true;
    }

    /* ---- height ----
       Gated. Measurements refine a silhouette; they do not select one.
       Previously this had no condition at all, so every visitor was
       asked their height and weight before being shown a single
       garment — which reads as an interrogation, not a fitting.
       Now it waits until the brief is real (garment + occasion known)
       and pieces have actually been presented. */
    if (!profile.height && profile.outfitType && profile.event && profile.shownHandles.length > 0) {
      profile.lastQuestion = 'height';
      saveState();
      novaSay(
        say(
          'To refine the silhouette, what is your approximate height?',
          'Ab silhouette properly judge karne ke liye aapki approximate height?'
        ),
        [{ label: say('Skip', 'Skip'), value: 'skip height' }]
      );
      return true;
    }

    /* ---- weight ---- Follows height, never opens the conversation. */
    if (!profile.weight && profile.height && profile.height !== 'Not provided') {
      profile.lastQuestion = 'weight';
      saveState();
      novaSay(
        say(
          'And your approximate weight? It does not need to be exact.',
          'Aur approximate weight? Exact hona zaroori nahi.'
        ),
        [{ label: say('Skip', 'Skip'), value: 'skip weight' }]
      );
      return true;
    }

    /* ---- complexion ---- */
    /* ---- complexion ---- Only meaningful once colour is in play. */
    if (!profile.complexion && profile.colour && profile.shownHandles.length > 0) {
      profile.lastQuestion = 'complexion';
      saveState();
      novaSay(
        say(
          'To refine the colour direction, which skin-tone range would you say is closest?',
          'Colour selection refine karne ke liye apni skin tone kis range ke closest kahenge?'
        ),
        [
          { label: 'Fair / Light', value: 'complexion light' },
          { label: 'Medium', value: 'complexion medium' },
          { label: 'Wheatish / Warm', value: 'complexion wheatish' },
          { label: 'Deep', value: 'complexion deep' },
          { label: say('Skip', 'Skip'), value: 'skip complexion' }
        ]
      );
      return true;
    }

    /* ---- budget ---- */
    if (!profile.budgetMin && !profile.budgetMax && !profile.budgetText) {
      profile.lastQuestion = 'budget';
      saveState();
      novaSay(
        say(
          'One last detail — what approximate budget range should I keep the recommendations within?',
          'Last thing — kis approximate budget range ke andar options rakhun?'
        ),
        [
          { label: 'Under 100K', value: 'budget under 100k' },
          { label: '100K–150K', value: 'budget 100k to 150k' },
          { label: '150K–250K', value: 'budget 150k to 250k' },
          { label: '250K+', value: 'budget 250k plus' },
          { label: say('Flexible', 'Flexible'), value: 'budget flexible' }
        ]
      );
      return true;
    }

    return false;
  }

  /* ==========================================================
     14. ANSWER HANDLING
     Interpret a reply in the context of the active question,
     which is how V12 disambiguated bare answers like "20 days".
     ========================================================== */

  function handleQuestionAnswer(text, reading) {
    var question = profile.lastQuestion;
    if (!question) return;

    var folded = fold(text);

    // "Skip" is always honoured, and recorded so the flow moves on.
    if (reading.has('skip')) {
      if (question === 'height') profile.height = 'Not provided';
      if (question === 'weight') profile.weight = 'Not provided';
      if (question === 'complexion') profile.complexion = 'Not provided';
      if (question === 'colour') profile.colour = 'NOVA Selection';
      if (question === 'budget') profile.budgetText = 'Flexible';
      profile.lastQuestion = null;
      saveState();
      return;
    }

    switch (question) {
      case 'role':
        if (reading.has('ownWedding') || /my wedding|meri/.test(folded)) profile.role = 'Groom';
        else if (reading.has('guestWedding') || /someone|else|kisi/.test(folded)) profile.role = 'Wedding Guest';
        break;

      case 'timeline':
        parseTimeline(text);
        break;

      case 'location':
        if (reading.has('international')) profile.locationType = 'International';
        else if (reading.has('pakistan')) profile.locationType = 'Pakistan';
        break;

      case 'outfit':
        if (reading.has('recommend') || /suggest for me|aap suggest/.test(folded)) {
          profile.needsRecommendation = true;
        }
        break;

      case 'colour':
        if (/choose colour for me|aap choose/.test(folded)) profile.colour = 'NOVA Selection';
        break;

      case 'height':
        extractMeasurements(text);
        // A bare number in answer to "height" is a height.
        if (!profile.height) {
          var h = folded.match(/^(\d{1,3})(?:\s*cm)?$/);
          if (h) profile.height = Number(h[1]) > 100 ? h[1] + ' cm' : h[1] + "'";
        }
        break;

      case 'weight':
        extractMeasurements(text);
        if (!profile.weight) {
          var w = folded.match(/^(\d{2,3})$/);
          if (w) profile.weight = w[1] + ' kg';
        }
        break;

      case 'budget':
        if (/flexible/.test(folded)) profile.budgetText = 'Flexible';
        else parseBudget(text);
        break;
    }

    profile.lastQuestion = null;
    saveState();
  }

  /* ==========================================================
     15. SERVICE AND EXPERIENCE INTENTS
     Answerable at any point in the conversation.
     ========================================================== */

  function handleServiceIntent(reading) {
    if (reading.has('human') || reading.has('appointment')) {
      updateHumanLink();
      if (!CONFIG.whatsapp) {
        return novaSay(say(
          'I can put you in touch with the atelier. The contact details in the footer of every page will reach the team directly.',
          'Main aapko atelier se connect kar sakta hoon. Har page ke footer mein contact details maujood hain.'
        ));
      }
      return novaSay(
        say('Of course. I will pass your brief across so you do not have to repeat it.',
          'Bilkul. Main aapka poora brief aage bhej dunga taake aapko dobara batana na pare.'),
        [{ label: say('Open WhatsApp', 'WhatsApp kholein'), value: '__whatsapp' }]
      );
    }

    if (reading.has('store')) {
      if (!CONFIG.map) {
        return novaSay(say(
          'Our atelier is in Gujranwala, Pakistan. The full address is in the footer of every page.',
          'Hamara atelier Gujranwala, Pakistan mein hai. Poora address har page ke footer mein hai.'
        ));
      }
      return novaSay(
        say('Our atelier is in Gujranwala, Pakistan.', 'Hamara atelier Gujranwala, Pakistan mein hai.'),
        [{ label: say('Get directions', 'Directions lein'), value: '__map' }]
      );
    }

    if (reading.has('delivery')) {
      if (profile.daysRemaining && profile.locationType) {
        var assessment = deliveryAssessment();
        if (assessment) return novaSay(assessment);
      }
      return novaSay(
        say(
          'Timelines depend on the piece and the work involved, so the Design Team confirms a date once your measurements are taken. Tell me your event date and where you need it, and I will tell you which window it falls into.',
          'Timeline piece aur uske kaam par depend karti hai, isliye Design Team measurements ke baad exact date confirm karti hai. Mujhe event date aur delivery location batayein, main window bata dunga.'
        ),
        [
          { label: 'Pakistan', value: 'Pakistan' },
          { label: 'International', value: 'International' }
        ]
      );
    }

    if (reading.has('measurement') || reading.has('custom')) {
      return novaSay(
        say(
          'Every piece is made to your measurements. The Design Team guides you through them personally, whether you visit the atelier or send them from abroad.',
          'Har piece aapke measurements par banta hai. Design Team aapko personally guide karti hai, chahe aap atelier aayein ya bahar se measurements bhejein.'
        ),
        CONFIG.whatsapp ? [{ label: say('Arrange measurements', 'Measurements arrange karein'), value: '__whatsapp' }] : null
      );
    }

    if (reading.has('price') || reading.has('budget')) {
      if (profile.budgetText) {
        return novaSay(say(
          'Noted — I am keeping options within ' + profile.budgetText + '. The price shown on each product page is current and accurate.',
          'Noted — main options ' + profile.budgetText + ' ke andar rakh raha hoon. Har product page par price current aur accurate hai.'
        ));
      }
      return novaSay(
        say(
          'Every piece is priced individually, and the price on each product page is current. Tell me a range you are comfortable with and I will keep the options inside it.',
          'Har piece ki apni pricing hai, aur product page par price current hota hai. Mujhe apni comfortable range batayein, main usi ke andar options rakhunga.'
        ),
        [
          { label: 'Under 100K', value: 'budget under 100k' },
          { label: '100K–150K', value: 'budget 100k to 150k' },
          { label: '150K–250K', value: 'budget 150k to 250k' },
          { label: say('Flexible', 'Flexible'), value: 'budget flexible' }
        ]
      );
    }

    if (reading.has('care')) {
      return novaSay(say(
        'Embellished pieces are best dry cleaned by a specialist and stored flat, away from direct light. Care guidance specific to your piece is confirmed by the Design Team with your order.',
        'Embellished pieces specialist dry cleaner se clean karwayein aur flat, direct light se door store karein. Aapke piece ki specific care guidance Design Team order ke saath confirm karti hai.'
      ));
    }

    if (reading.has('order')) {
      return novaSay(
        say(
          'Order updates come from the Design Team directly. Share your order number with them and they will trace it for you.',
          'Order updates Design Team se aate hain. Unhein apna order number bhejein, woh trace kar denge.'
        ),
        CONFIG.whatsapp ? [{ label: say('Ask about my order', 'Order ke baare mein poochein'), value: '__whatsapp' }] : null
      );
    }

    if (reading.has('world')) {
      window.location.href = CONFIG.worldUrl;
      return Promise.resolve();
    }

    return null;
  }

  function openCustomerAccount() {
    // Shopify's own customer authentication. NOVA never builds a
    // parallel login and never stores credentials.
    window.location.href = CONFIG.accountUrl;
  }

  /* ==========================================================
     16. NOTIFICATIONS

     Front-end permission flow only.

     There is no push-subscription backend in this theme, so NOVA
     does not claim remote push works. What the browser genuinely
     supports — a local Notification permission — is offered
     honestly, contextually, and never on first contact.
     ========================================================== */

  var notifyState = (function () {
    try {
      return JSON.parse(window.localStorage.getItem(NOTIFY_KEY)) || {};
    } catch (e) {
      return {};
    }
  })();

  function saveNotifyState() {
    try {
      window.localStorage.setItem(NOTIFY_KEY, JSON.stringify(notifyState));
    } catch (e) {}
  }

  function notificationsSupported() {
    return typeof window.Notification !== 'undefined' && window.isSecureContext !== false;
  }

  /**
   * The invitation appears only once the conversation has earned
   * it: a real brief exists, several turns have happened, and the
   * client has not already answered. Never on arrival.
   */
  function maybeInviteNotifications() {
    if (!notificationsSupported()) return;
    if (notifyState.dismissed || notifyState.granted) return;
    if (window.Notification.permission !== 'default') return;
    if (!view.notify) return;

    var turns = history.filter(function (m) { return m.role === 'user'; }).length;
    var hasBrief = !!(profile.occasion && (profile.event || profile.outfitType));

    if (turns < 3 || !hasBrief) return;

    // Respect a "not now" for a week.
    if (notifyState.snoozedUntil && Date.now() < notifyState.snoozedUntil) return;

    view.notify.hidden = false;
    view.notify.setAttribute('aria-hidden', 'false');
    profile.notifyPromptedAt = Date.now();
    saveState();
  }

  function hideNotifyInvitation() {
    if (!view.notify) return;
    view.notify.hidden = true;
    view.notify.setAttribute('aria-hidden', 'true');
  }

  /**
   * Called only from a click on Allow, so the browser prompt is
   * always a direct consequence of user action.
   */
  function requestNotifications() {
    hideNotifyInvitation();

    if (!notificationsSupported()) {
      return novaSay(say(
        'This browser does not support notifications.',
        'Yeh browser notifications support nahi karta.'
      ));
    }

    if (window.Notification.permission === 'granted') {
      notifyState.granted = true;
      saveNotifyState();
      return novaSay(say(
        'Notifications are already enabled for this device.',
        'Is device par notifications pehle se enabled hain.'
      ));
    }

    if (window.Notification.permission === 'denied') {
      return novaSay(say(
        'Notifications are blocked in your browser settings. You can re-enable them from this site’s permissions.',
        'Notifications browser settings mein blocked hain. Aap site permissions se dobara allow kar sakte hain.'
      ));
    }

    return window.Notification.requestPermission()
      .then(function (permission) {
        if (permission === 'granted') {
          notifyState.granted = true;
          saveNotifyState();
          // Stated plainly: this device, this browser. No claim of
          // push delivery, because no push backend exists.
          return novaSay(say(
            'Enabled. I can now notify you on this device while your browser is open. Order updates still come from the Design Team directly.',
            'Enable ho gaya. Ab main is device par notify kar sakta hoon jab browser open ho. Order updates phir bhi Design Team se aate hain.'
          ));
        }

        notifyState.dismissed = true;
        saveNotifyState();
        return novaSay(say('No problem — nothing was enabled.', 'Koi baat nahi — kuch enable nahi kiya gaya.'));
      })
      .catch(function () {
        return novaSay(say(
          'I could not open the permission prompt just now.',
          'Permission prompt abhi open nahi ho saki.'
        ));
      });
  }

  function snoozeNotifications() {
    hideNotifyInvitation();
    notifyState.snoozedUntil = Date.now() + 7 * 24 * 60 * 60 * 1000;
    saveNotifyState();
  }

  /* ==========================================================
     17. ATELIER HANDOFF
     The whole brief travels with the client so nothing has to be
     repeated. V12 behaviour, preserved.
     ========================================================== */

  function briefSummary(question) {
    return [
      'Hello Ahsan Tariq Couture Design Team.',
      'I was speaking with NOVA.',
      '',
      profile.occasion ? 'Occasion: ' + profile.occasion : '',
      profile.role ? 'Role: ' + profile.role : '',
      profile.event ? 'Event: ' + profile.event : '',
      profile.timelineText ? 'Timeline: ' + profile.timelineText : '',
      profile.locationType ? 'Delivery: ' + profile.locationType : '',
      profile.outfitType ? 'Outfit: ' + profile.outfitType : '',
      profile.style ? 'Style: ' + profile.style : '',
      profile.workLevel ? 'Work: ' + profile.workLevel : '',
      profile.colour ? 'Colour: ' + profile.colour : '',
      profile.fit ? 'Fit: ' + profile.fit : '',
      profile.height && profile.height !== 'Not provided' ? 'Height: ' + profile.height : '',
      profile.weight && profile.weight !== 'Not provided' ? 'Weight: ' + profile.weight : '',
      profile.complexion && profile.complexion !== 'Not provided' ? 'Complexion: ' + profile.complexion : '',
      profile.budgetText ? 'Budget: ' + profile.budgetText : '',
      question ? 'Question: ' + question : ''
    ]
      .filter(Boolean)
      .join('\n');
  }

  function updateHumanLink(question) {
    if (!view.humanButton) return;

    if (!CONFIG.whatsapp) {
      view.humanButton.hidden = true;
      return;
    }

    view.humanButton.href =
      'https://wa.me/' + CONFIG.whatsapp + '?text=' + encodeURIComponent(briefSummary(question));
  }

  function openWhatsApp() {
    if (!CONFIG.whatsapp) {
      return novaSay(say(
        'WhatsApp is not configured for this store yet.',
        'Is store ke liye WhatsApp abhi configure nahi hua.'
      ));
    }

    window.open(
      'https://wa.me/' + CONFIG.whatsapp + '?text=' + encodeURIComponent(briefSummary()),
      '_blank',
      'noopener'
    );

    return novaSay(say('Opening WhatsApp with your brief attached.', 'WhatsApp khol raha hoon, aapka brief saath hai.'));
  }

  function openMap() {
    if (!CONFIG.map) {
      return novaSay(say(
        'The atelier address is in the footer of every page.',
        'Atelier ka address har page ke footer mein hai.'
      ));
    }
    window.open(CONFIG.map, '_blank', 'noopener');
    return novaSay(say('Opening directions.', 'Directions khol raha hoon.'));
  }

  /* ==========================================================
     18. IMAGE, CAMERA AND VOICE
     Restored from V12.

     An attached image is shown to the client and sent with the
     brief to the Design Team. NOVA does not pretend to analyse
     it — no fabricated image understanding.
     ========================================================== */

  var pendingImage = null;

  function setImage(file) {
    if (!file) return;

    if (!/^image\//.test(file.type)) {
      novaSay(say('That file is not an image.', 'Yeh file image nahi hai.'));
      return;
    }

    // 10 MB ceiling: a phone photo is well under this and the
    // object URL is only ever held in memory.
    if (file.size > 10 * 1024 * 1024) {
      novaSay(say('That image is too large. Please try one under 10 MB.', 'Yeh image bohat bari hai. 10 MB se kam try karein.'));
      return;
    }

    if (pendingImage && pendingImage.url) URL.revokeObjectURL(pendingImage.url);

    var url = URL.createObjectURL(file);
    pendingImage = { file: file, url: url };

    if (view.preview && view.previewImg) {
      view.previewImg.src = url;
      view.preview.hidden = false;
      view.preview.setAttribute('aria-hidden', 'false');
    }
  }

  function clearImage() {
    if (pendingImage && pendingImage.url) URL.revokeObjectURL(pendingImage.url);
    pendingImage = null;

    if (view.preview) {
      view.preview.hidden = true;
      view.preview.setAttribute('aria-hidden', 'true');
    }
    if (view.previewImg) view.previewImg.removeAttribute('src');
    if (view.fileInput) view.fileInput.value = '';
    if (view.cameraInput) view.cameraInput.value = '';
  }

  function acknowledgeImage() {
    if (!pendingImage) return null;

    clearImage();

    return novaSay(
      say(
        'I can see you have attached a reference. I will not guess at it — send it to the Design Team with your brief and they will tell you exactly what is achievable.',
        'Aapne reference attach kiya hai. Main iska andaza nahi lagaunga — ise apne brief ke saath Design Team ko bhejein, woh exactly bata denge kya possible hai.'
      ),
      CONFIG.whatsapp ? [{ label: say('Send to the atelier', 'Atelier ko bhejein'), value: '__whatsapp' }] : null
    );
  }

  function bindMediaInputs() {
    var uploadButton = root.querySelector('[data-nova-upload]');
    var cameraButton = root.querySelector('[data-nova-camera-open]');
    var removeButton = root.querySelector('[data-nova-remove-image]');

    if (uploadButton && view.fileInput) {
      uploadButton.addEventListener('click', function () { view.fileInput.click(); });
      view.fileInput.addEventListener('change', function () { setImage(view.fileInput.files[0]); });
    }

    if (cameraButton && view.cameraInput) {
      cameraButton.addEventListener('click', function () { view.cameraInput.click(); });
      view.cameraInput.addEventListener('change', function () { setImage(view.cameraInput.files[0]); });
    }

    if (removeButton) removeButton.addEventListener('click', clearImage);
  }

  function bindVoice() {
    var Recognition = window.SpeechRecognition || window.webkitSpeechRecognition;

    if (!view.voiceButton) return;

    if (!Recognition) {
      // Hidden rather than disabled: a control that cannot work
      // should not occupy the composer.
      view.voiceButton.hidden = true;
      return;
    }

    var recognition = new Recognition();
    recognition.lang = isRoman() ? 'ur-PK' : 'en-PK';
    recognition.interimResults = false;
    recognition.continuous = false;
    recognition.maxAlternatives = 1;

    var listening = false;

    function stopListening() {
      listening = false;
      view.voiceButton.classList.remove('is-listening');
      view.voiceButton.setAttribute('aria-pressed', 'false');
    }

    view.voiceButton.addEventListener('click', function () {
      if (listening) {
        try { recognition.stop(); } catch (e) {}
        stopListening();
        return;
      }

      try {
        recognition.lang = isRoman() ? 'ur-PK' : 'en-PK';
        recognition.start();
        listening = true;
        view.voiceButton.classList.add('is-listening');
        view.voiceButton.setAttribute('aria-pressed', 'true');
      } catch (e) {
        stopListening();
      }
    });

    recognition.onresult = function (event) {
      var transcript = event.results[0][0].transcript;
      if (view.input) {
        view.input.value = transcript;
        view.input.focus();
      }
      stopListening();
    };

    recognition.onerror = stopListening;
    recognition.onend = stopListening;
  }

  /* ==========================================================
     19. CONVERSATION TURN
     ========================================================== */

  var busy = false;

  function processMessage(text) {
    if (busy) return Promise.resolve();

    var message = String(text || '').trim();
    if (!message && !pendingImage) return Promise.resolve();

    busy = true;
    clearChoices();

    if (message) {
      bubble(message, 'user');
      remember('user', message);
      profile.language = detectLanguage(message) || profile.language;
    }

    // Choice-button shortcuts.
    if (message === '__whatsapp') return settle(openWhatsApp());
    if (message === '__map') return settle(openMap());
    if (message === '__account') { openCustomerAccount(); return settle(Promise.resolve()); }
    if (message === '__notify_allow') return settle(requestNotifications());
    if (message === '__notify_later') { snoozeNotifications(); return settle(Promise.resolve()); }

    var reading = analyse(message);
    profile.lastIntent = null;

    // An attached image is acknowledged before anything else.
    if (pendingImage && !message) return settle(acknowledgeImage());

    if (reading.has('reset')) {
      resetProfile();
      if (view.chat) view.chat.innerHTML = '';
      return settle(novaSay(
        say(
          'Starting fresh. Tell me what you are dressing for — English or Roman Urdu, whichever you prefer.',
          'Naye sire se shuru karte hain. Batayein kis liye dress karna hai — English ya Roman Urdu, jo aap ko theek lage.'
        ),
        openingChoices()
      ));
    }

    if (reading.has('bye')) {
      return settle(novaSay(say(
        'A pleasure. The atelier is here whenever you need us.',
        'Khushi hui. Atelier hamesha aapke liye maujood hai.'
      )));
    }

    if (reading.has('thanks')) {
      return settle(novaSay(say(
        'With pleasure. Anything else I can help you consider?',
        'Khushi se. Aur kuch dekhna chahenge?'
      )));
    }

    if (reading.has('account')) { openCustomerAccount(); return settle(Promise.resolve()); }

    if (reading.has('notifications')) return settle(requestNotifications());

    /* Once a recommendation has been made, short replies such as
       "okay", "yes", "show me" and "more" must ADVANCE the
       experience. They must never restart the questionnaire.
       (V12 rule, preserved.) */
    if (profile.conversationStage === 'recommendation' || profile.conversationStage === 'products') {
      if (reading.has('more') || reading.has('yes') || reading.has('show')) {
        return settle(showMatchingProducts(true));
      }
    }

    // Answer the active question first, then mine the same
    // sentence for anything else it happens to contain.
    if (profile.lastQuestion) handleQuestionAnswer(message, reading);
    extractFacts(reading);
    saveState();

    var service = handleServiceIntent(reading);
    if (service) return settle(service);

    if (reading.has('recommend')) {
      profile.needsRecommendation = true;
      saveState();
    }

    // A direct product ask answers from the catalogue immediately
    // rather than starting an interview.
    var garment = reading.strongest(['sherwani', 'princeCoat', 'suit', 'kurta', 'openJacket', 'waistcoat']);
    if (reading.has('show') && garment) {
      return settle(
        searchCatalogue(GARMENT_LABELS[garment], MAX_PRODUCTS).then(function (products) {
          if (!products.length) return advance();

          profile.conversationStage = 'products';
          saveState();

          return novaSay(say(
            'Our ' + GARMENT_LABELS[garment].toLowerCase() + ' pieces.',
            'Hamare ' + GARMENT_LABELS[garment].toLowerCase() + ' pieces.'
          )).then(function () {
            renderProducts(products);
            renderChoices([
              { label: say('Refine for my occasion', 'Mere occasion ke liye refine karein'), value: 'help me choose' },
              { label: say('Show me more', 'Aur dikhayein'), value: 'show me more' }
            ]);
            maybeInviteNotifications();
          });
        })
      );
    }

    if (reading.has('greeting') && Object.keys(reading.scores).length <= 1) {
      return settle(novaSay(
        say(
          'Hello. Tell me what you are dressing for and I will guide you personally.',
          'Assalam o alaikum. Batayein kis moqay ke liye dress karna hai, main personally guide karunga.'
        ),
        openingChoices()
      ));
    }

    return settle(advance());
  }

  /** Move the conversation forward: ask, or advise. */
  function advance() {
    if (nextQuestion()) return Promise.resolve();
    return buildRecommendation();
  }

  function settle(promise) {
    return Promise.resolve(promise).then(
      function () { busy = false; maybeInviteNotifications(); },
      function () { busy = false; }
    );
  }

  /* ---- the recommendation itself ---- */

  function buildRecommendation() {
    var outfit = chooseOutfitDirection();
    var colour = colourAdvice();

    profile.conversationStage = 'recommendation';
    saveState();

    var lead;

    if (isRoman()) {
      lead = 'Ab direction clear hai. ' +
        (profile.event ? profile.event + ' ke liye ' : '') +
        'main ' + outfit + ' recommend karunga' +
        (profile.style ? ', ' + profile.style.toLowerCase() + ' mood ke saath' : '') +
        '. Colour ke liye ' + colour + ' sab se behtar baithega' +
        (profile.complexion && profile.complexion !== 'Not provided'
          ? ' aapki ' + profile.complexion.toLowerCase() + ' skin tone ke saath'
          : '') +
        '.';
    } else {
      lead = 'The direction is clear now. For ' +
        (profile.event ? 'your ' + profile.event.toLowerCase() : 'this occasion') +
        ' I would put you in ' + outfit +
        (profile.style ? ', with a ' + profile.style.toLowerCase() + ' presence' : '') +
        '. On colour, ' + colour + ' will sit best' +
        (profile.complexion && profile.complexion !== 'Not provided'
          ? ' against your ' + profile.complexion.toLowerCase() + ' skin tone'
          : '') +
        '.';
    }

    if (profile.workLevel) {
      lead += isRoman()
        ? ' Embellishment ' + profile.workLevel.toLowerCase() + ' rakhenge.'
        : ' I would keep the embellishment ' + profile.workLevel.toLowerCase() + '.';
    }

    return novaSay(lead).then(function () {
      return showMatchingProducts(false);
    });
  }

  function showMatchingProducts(force) {
    return rankProducts(MAX_PRODUCTS).then(function (products) {
      if (!products.length) {
        var terms = recommendationTerms();

        // Fall back to a text search before admitting defeat.
        return searchCatalogue(terms.join(' '), MAX_PRODUCTS).then(function (fallback) {
          if (fallback.length) {
            profile.conversationStage = 'products';
            saveState();
            renderProducts(fallback, say('Closest in the current collection', 'Maujooda collection mein sab se qareeb'));
            renderChoices(afterProductChoices());
            return;
          }

          return novaSay(
            say(
              'I could not find a close match in the current collection. That is exactly what the atelier is for — send your brief across and the Design Team will create to your measurements.',
              'Maujooda collection mein exact match nahi mila. Atelier isi liye hai — apna brief bhejein, Design Team aapke measurements par banayegi.'
            ),
            CONFIG.whatsapp
              ? [
                  { label: say('Talk to the atelier', 'Atelier se baat karein'), value: '__whatsapp' },
                  { label: say('Start again', 'Dobara shuru karein'), value: 'start over' }
                ]
              : [{ label: say('Start again', 'Dobara shuru karein'), value: 'start over' }]
          );
        });
      }

      profile.conversationStage = 'products';
      saveState();

      renderProducts(
        products,
        force ? say('More from the collection', 'Collection se aur') : say('Selected for your brief', 'Aapke brief ke liye chuna gaya')
      );
      renderChoices(afterProductChoices());
      updateHumanLink();
    });
  }

  function afterProductChoices() {
    var choices = [
      { label: say('Show me more', 'Aur dikhayein'), value: 'show me more' },
      { label: say('Something lighter', 'Thora light'), value: 'light minimal' },
      { label: say('Something heavier', 'Thora heavy'), value: 'heavy' }
    ];

    if (CONFIG.whatsapp) {
      choices.push({ label: say('Talk to the atelier', 'Atelier se baat karein'), value: '__whatsapp' });
    }

    return choices;
  }

  function openingChoices() {
    return [
      { label: say('Groom', 'Dulha'), value: 'my wedding i am the groom' },
      { label: say('Wedding guest', 'Wedding guest'), value: 'someone else wedding guest' },
      { label: 'Sherwani', value: 'show me sherwani' },
      { label: say('Prince coat', 'Prince coat'), value: 'show me prince coat' },
      { label: say('Suits', 'Suits'), value: 'show me suit' },
      { label: say('Help me choose', 'Aap suggest karein'), value: 'help me choose' }
    ];
  }

  /* ==========================================================
     PANEL, WAKE TRANSITION, BOOT
     ========================================================== */

  var greeted = false;

  function pageContext() {
    var body = document.body;
    var context = { type: null, title: null };

    if (body.classList.contains('template-product')) {
      context.type = 'product';
      var h = document.querySelector('.at-pdp__title, .product-single__title, h1');
      if (h) context.title = h.textContent.trim();
    } else if (body.classList.contains('template-collection')) {
      context.type = 'collection';
      var c = document.querySelector('.at-collection-hero__title, .collection-hero__title, h1');
      if (c) context.title = c.textContent.trim();
    }

    return context;
  }

  function timeOfDay() {
    var hour = new Date().getHours();
    if (hour < 12) return say('morning', 'subah');
    if (hour < 17) return say('afternoon', 'dopahar');
    return say('evening', 'shaam');
  }

  function greet() {
    var context = pageContext();
    var line;

    if (isRoman()) {
      line = 'Assalam o alaikum. Main NOVA hoon.';
      if (context.type === 'product' && context.title) {
        line += ' Aap ' + context.title + ' dekh rahe hain — main is ke baare mein bata sakta hoon, ya kuch aur qareeb dhoond sakta hoon.';
      } else if (context.type === 'collection' && context.title) {
        line += ' Aap ' + context.title + ' mein hain. Occasion batayein, main narrow kar deta hoon.';
      } else {
        line += ' Batayein kis moqay ke liye dress karna hai — English ya Roman Urdu, jo aap ko theek lage.';
      }
    } else {
      line = 'Good ' + timeOfDay() + '. I am NOVA.';
      if (context.type === 'product' && context.title) {
        line += ' You are looking at ' + context.title + ' — I can talk you through it, or find something closer to what you need.';
      } else if (context.type === 'collection' && context.title) {
        line += ' You are in ' + context.title + '. Tell me the occasion and I will narrow it for you.';
      } else {
        line += ' Tell me what you are dressing for, in English or Roman Urdu, and I will guide you personally.';
      }
    }

    novaSay(line, openingChoices());
  }

  /**
   * Wake transition.
   *
   * A brief cosmic settle before the conversation appears. It is
   * short by design (620ms), skipped entirely under reduced
   * motion, and interruptible — any click, key or the panel
   * closing ends it immediately, so it can never trap anyone.
   */
  function playWake() {
    if (!view.awake) return Promise.resolve();

    if (prefersReducedMotion()) {
      view.awake.hidden = true;
      return Promise.resolve();
    }

    view.awake.hidden = false;
    view.awake.setAttribute('aria-hidden', 'true');
    root.classList.add('is-waking');

    return new Promise(function (resolve) {
      var done = false;

      function finish() {
        if (done) return;
        done = true;

        root.classList.remove('is-waking');
        if (view.awake) view.awake.hidden = true;

        document.removeEventListener('keydown', finish, true);
        if (view.panel) view.panel.removeEventListener('click', finish, true);

        resolve();
      }

      // Interruptible.
      document.addEventListener('keydown', finish, true);
      if (view.panel) view.panel.addEventListener('click', finish, true);

      window.setTimeout(finish, 620);
    });
  }

  function openNova(trigger) {
    if (view.isOpen || !view.panel) return;

    view.isOpen = true;
    root.classList.add('is-open');
    root.setAttribute('data-open', 'true');
    document.documentElement.classList.add('at-nova-active');
    view.panel.setAttribute('aria-hidden', 'false');

    Array.prototype.slice.call(document.querySelectorAll('[data-nova-open]')).forEach(function (button) {
      button.setAttribute('aria-expanded', 'true');
    });

    if (window.AURUM && window.AURUM.scrollLock) window.AURUM.scrollLock.lock();

    if (!view.trap && window.AURUM && window.AURUM.createFocusTrap) {
      view.trap = window.AURUM.createFocusTrap(view.panel);
    }

    playWake().then(function () {
      if (!view.isOpen) return;
      if (view.trap) view.trap.activate();

      if (!greeted) {
        greeted = true;
        if (!restoreHistory()) greet();
        else maybeInviteNotifications();
      }
    });

    updateHumanLink();
    fetchCatalogue();
  }

  function closeNova() {
    if (!view.isOpen || !view.panel) return;

    view.isOpen = false;
    root.classList.remove('is-open', 'is-waking');
    root.setAttribute('data-open', 'false');
    document.documentElement.classList.remove('at-nova-active');
    view.panel.setAttribute('aria-hidden', 'true');
    if (view.awake) view.awake.hidden = true;

    Array.prototype.slice.call(document.querySelectorAll('[data-nova-open]')).forEach(function (button) {
      button.setAttribute('aria-expanded', 'false');
    });

    if (window.AURUM && window.AURUM.scrollLock) window.AURUM.scrollLock.unlock();
    if (view.trap) view.trap.release();
  }

  function addToBag(variantId, title, button) {
    button.disabled = true;
    button.classList.add('is-adding');

    var routes = window.routes || {};

    fetch(routes.cart_add_url || '/cart/add', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] })
    })
      .then(function (response) { return response.json(); })
      .then(function (result) {
        if (result.status) throw new Error(result.description || 'add');

        bubble(say(title + ' added to your bag.', title + ' aapke bag mein add ho gaya.'), 'nova');

        return fetch((routes.cart_url || '/cart') + '.js')
          .then(function (r) { return r.json(); })
          .then(function (cart) {
            if (window.AURUM && window.AURUM.cart) window.AURUM.cart.syncCount(cart.item_count);
          });
      })
      .catch(function () {
        bubble(say(
          'I could not add that to your bag. It may no longer be available.',
          'Yeh bag mein add nahi ho saka. Shayad ab available nahi hai.'
        ), 'nova');
      })
      .finally(function () {
        button.disabled = false;
        button.classList.remove('is-adding');
      });
  }

  function bind() {
    document.addEventListener('click', function (event) {
      var opener = event.target.closest('[data-nova-open], [data-nova-open-external]');
      if (opener) {
        event.preventDefault();
        openNova(opener);
        return;
      }

      if (event.target.closest('[data-nova-close]')) {
        event.preventDefault();
        closeNova();
        return;
      }

      var choice = event.target.closest('[data-nova-choice]');
      if (choice && root.contains(choice)) {
        event.preventDefault();
        processMessage(choice.dataset.novaChoice);
        return;
      }

      var add = event.target.closest('[data-nova-add]');
      if (add && root.contains(add)) {
        event.preventDefault();
        addToBag(add.dataset.novaAdd, add.dataset.novaAddTitle, add);
        return;
      }

      if (event.target.closest('[data-nova-notify-allow]')) {
        event.preventDefault();
        requestNotifications();
        return;
      }

      if (event.target.closest('[data-nova-notify-later]')) {
        event.preventDefault();
        snoozeNotifications();
      }
    });

    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape' && view.isOpen) {
        event.preventDefault();
        closeNova();
      }
    });

    if (view.form) {
      view.form.addEventListener('submit', function (event) {
        event.preventDefault();
        var value = view.input ? view.input.value : '';

        if (!value.trim() && pendingImage) {
          processMessage('');
          return;
        }

        if (!value.trim()) return;
        if (view.input) view.input.value = '';
        processMessage(value);
      });
    }

    bindMediaInputs();
    bindVoice();
    updateHumanLink();
  }

  bind();

  /* Public surface. AT_NOVA is the V12 name and is kept so any
     existing integration continues to work; NOVA is the new one. */
  var api = {
    open: openNova,
    close: closeNova,
    ask: function (message) {
      openNova();
      return processMessage(message);
    },
    getProfile: function () {
      return JSON.parse(JSON.stringify(profile));
    },
    account: function () {
      openNova();
      openCustomerAccount();
    },
    notifications: function () {
      openNova();
      return requestNotifications();
    },
    products: function () {
      openNova();
      return showMatchingProducts(true);
    },
    reset: function () {
      resetProfile();
      if (view.chat) view.chat.innerHTML = '';
      greeted = false;
      openNova();
    }
  };

  window.NOVA = api;
  window.AT_NOVA = api;
})();